import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Link,
  TextField,
  Typography,
  Stack,
  Alert,
  Card as MuiCard,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import ForgotPassword from './components/ForgotPassword';
import API_BASE_URL from './config'; 

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(1),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  // CHANGE: 'height' to 'minHeight' to allow scrolling when content overflows
  minHeight: '100dvh', 
  
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn(props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email/username and password.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/token/`, {
        username: email,
        password: password,
      });

      const { access, refresh } = response.data;
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);

      window.location.href = '/';

    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Invalid credentials. Please check your details and try again.');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
      console.error('Sign-in error:', err);
    }
  };
  
  const handleRequestAccessClick = (event) => {
    event.preventDefault();
    alert(
      'Access to this application is by invitation only.\n\nPlease contact CityWise at contact@citywisestrategy.com to request an account.'
    );
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img src="/logo.svg" alt="Company Logo" style={{ height: '32px' }} />
          </Box>
          <Typography
            component="h1"
            variant="h5" 
            sx={{
              width: '100%',
              fontSize: 'clamp(1.75rem, 8vw, 1.875rem)',
              textAlign: 'center'
            }}
          >
            Sign In
          </Typography>
          <Typography component="p" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
            Access your Infrastructure Dashboard
          </Typography>

          {error && <Alert severity="error" sx={{ width: '100%', mt: 1 }}>{error}</Alert>}

          <Box
            component="form"
            onSubmit={handleEmailSignIn}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
              mt: 1,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email / Username</FormLabel>
              <TextField
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
            >
              Sign in
            </Button>
          </Box>

          {/* --- SECURITY FIX: Moved ForgotPassword outside of the form --- */}
          <ForgotPassword open={open} handleClose={handleClose} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mt: 1,
            }}
          >
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
            >
              Forgot your password?
            </Link>
            <Link
              href="#"
              variant="body2"
              onClick={handleRequestAccessClick}
            >
              Need an account?
            </Link>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}